import { useEffect, useRef, useState } from "react";
import Bluelagoon from "../Bluelagoon.js";

const MAX_RECONNECT_DELAY = 15000;
const MAX_RECONNECT_ATTEMPTS = 10;
const POLL_INTERVAL = 5000; // 5s polling when WS is unavailable

/**
 * React hook for real-time task status updates via WebSocket.
 * Automatically reconnects with exponential backoff on disconnect.
 * Falls back to HTTP polling after MAX_RECONNECT_ATTEMPTS failures.
 *
 * Works across multiple servers (no sticky sessions needed — any server
 * that receives the PG NOTIFY will broadcast to its connected clients).
 *
 * @param {Object} options
 * @param {string} options.taskId - Task public key to subscribe to
 * @param {function} options.onUpdate - Callback when task status changes
 * @param {boolean} options.enabled - Whether the hook is active
 * @returns {{ isConnected: boolean, connectionFailed: boolean, isPolling: boolean }}
 */
export function useTaskWebSocket({ taskId, onUpdate, enabled = true }) {
    const wsRef = useRef(null);
    const onUpdateRef = useRef(onUpdate);
    const reconnectAttemptRef = useRef(0);
    const reconnectTimerRef = useRef(null);
    const pollTimerRef = useRef(null);
    const lastFeedbackLenRef = useRef(0);
    const isCleanedUpRef = useRef(false);
    const [isConnected, setIsConnected] = useState(false);
    const [connectionFailed, setConnectionFailed] = useState(false);
    const [isPolling, setIsPolling] = useState(false);

    // Keep callback ref current without re-triggering effect
    useEffect(() => {
        onUpdateRef.current = onUpdate;
    }, [onUpdate]);

    useEffect(() => {
        if (!taskId || !enabled) {
            return;
        }

        isCleanedUpRef.current = false;
        lastFeedbackLenRef.current = 0;
        setConnectionFailed(false);
        setIsConnected(false);
        setIsPolling(false);

        function connect() {
            if (isCleanedUpRef.current) return;

            Bluelagoon.connectTaskWebSocket(taskId, (data) => {
                if (data.type === "task_update") {
                    onUpdateRef.current(data);
                }
            }).then((ws) => {
                if (isCleanedUpRef.current) {
                    if (ws) ws.close();
                    return;
                }

                if (!ws) {
                    handleWsFailed();
                    return;
                }

                wsRef.current = ws;

                // WS may already be open by the time we attach listeners
                // (ticket fetch is async, connection can be fast)
                if (ws.readyState === WebSocket.OPEN) {
                    reconnectAttemptRef.current = 0;
                    setIsConnected(true);
                    setConnectionFailed(false);
                    stopPolling();
                }

                ws.addEventListener("open", () => {
                    reconnectAttemptRef.current = 0;
                    setIsConnected(true);
                    setConnectionFailed(false);
                    stopPolling();
                });

                ws.addEventListener("close", () => {
                    setIsConnected(false);
                    wsRef.current = null;
                    scheduleReconnect();
                });

                ws.addEventListener("error", () => {
                    // error is always followed by close, so reconnect happens there
                    setConnectionFailed(true);
                    setIsConnected(false);
                });
            });
        }

        function handleWsFailed() {
            setConnectionFailed(true);
            startPolling();
        }

        function scheduleReconnect() {
            if (isCleanedUpRef.current) return;

            if (reconnectAttemptRef.current >= MAX_RECONNECT_ATTEMPTS) {
                // WS is not working — fall back to HTTP polling
                startPolling();
                return;
            }

            const delay = Math.min(
                1000 * Math.pow(2, reconnectAttemptRef.current),
                MAX_RECONNECT_DELAY
            );
            reconnectAttemptRef.current += 1;

            reconnectTimerRef.current = setTimeout(() => {
                reconnectTimerRef.current = null;
                connect();
            }, delay);
        }

        function startPolling() {
            if (isCleanedUpRef.current || pollTimerRef.current) return;

            setIsPolling(true);

            async function poll() {
                if (isCleanedUpRef.current) return;

                try {
                    const res = await Bluelagoon.getTaskStatus({ taskId });
                    if (isCleanedUpRef.current) return;

                    if (res?.data) {
                        const feedbackLen = res.data.partialFeedback?.length || 0;
                        const statusChanged = res.data.status !== "RUNNING" && res.data.status !== "PENDING";
                        const feedbackChanged = feedbackLen > lastFeedbackLenRef.current;

                        if (statusChanged || feedbackChanged) {
                            lastFeedbackLenRef.current = feedbackLen;
                            onUpdateRef.current({
                                type: "task_update",
                                taskId,
                                data: res.data,
                            });
                        }

                        // Stop polling if terminal status
                        if (res.data.status === "COMPLETED" || res.data.status === "FAILED" || res.data.status === "CANCELLED") {
                            stopPolling();
                            return;
                        }
                    }
                } catch {
                    // Ignore poll errors — will retry next interval
                }

                if (!isCleanedUpRef.current) {
                    pollTimerRef.current = setTimeout(poll, POLL_INTERVAL);
                }
            }

            poll();
        }

        function stopPolling() {
            if (pollTimerRef.current) {
                clearTimeout(pollTimerRef.current);
                pollTimerRef.current = null;
            }
            setIsPolling(false);
        }

        // Reconnect when browser comes back online
        const handleOnline = () => {
            if (isCleanedUpRef.current) return;
            if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
                reconnectAttemptRef.current = 0;
                stopPolling();
                connect();
            }
        };

        window.addEventListener("online", handleOnline);
        connect();

        return () => {
            isCleanedUpRef.current = true;
            window.removeEventListener("online", handleOnline);
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
            stopPolling();

            if (wsRef.current) {
                const ws = wsRef.current;
                wsRef.current = null;
                if (ws.readyState !== WebSocket.CLOSED && ws.readyState !== WebSocket.CLOSING) {
                    ws.close();
                }
            }
            setIsConnected(false);
        };
    }, [taskId, enabled]);

    return { isConnected, connectionFailed, isPolling };
}
